import type { TableTemplate } from '@pn/core/domain/table';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNil, uniq } from 'lodash-es';

type State = {
  isFetching: boolean;
  isError: boolean;
  tableFields: Record<string, string[] | undefined>;
  templates: TableTemplate[];
  selectedTemplates: Record<string, TableTemplate | undefined>;
};

const initialState: State = {
  isFetching: false,
  isError: false,
  tableFields: {},
  templates: [],
  selectedTemplates: {},
};

const createTableSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state) => {
        state.isFetching = true;
        state.isError = false;
      },
      receive: (
        state,
        { payload: templates }: PayloadAction<TableTemplate[]>
      ) => {
        state.isFetching = false;
        state.isError = false;
        state.templates = templates;
      },
      error: (state) => {
        state.isFetching = false;
        state.isError = true;
      },
      create: (state, { payload: template }: PayloadAction<TableTemplate>) => {
        state.templates.push(template);
      },
      remove: (state, { payload: template }: PayloadAction<TableTemplate>) => {
        state.templates = state.templates.filter((t) => t.id !== template.id);
      },
      select: (state, { payload: template }: PayloadAction<TableTemplate>) => {
        state.selectedTemplates[template.sourceLayerId] = template;
        state.tableFields[template.sourceLayerId] = template.fields;
      },
      updateTableFields: (
        state,
        {
          payload: { sourceLayerId, fields },
        }: PayloadAction<{ sourceLayerId: string; fields: string[] }>
      ) => {
        state.tableFields[sourceLayerId] = fields;
      },
      addTableFields: (
        state,
        {
          payload: { sourceLayerId, fields },
        }: PayloadAction<{ sourceLayerId: string; fields: string[] }>
      ) => {
        /**
         * The nil check fixes a bug that causes the layer to not have its
         * default fields set when `addTableFields` is called beforehand.
         */
        if (isNil(state.tableFields[sourceLayerId])) return;

        state.tableFields[sourceLayerId] = uniq([
          ...state.tableFields[sourceLayerId],
          ...fields,
        ]);
      },
      removeTableFields: (
        state,
        {
          payload: { sourceLayerId, fields },
        }: PayloadAction<{ sourceLayerId: string; fields: string[] }>
      ) => {
        state.tableFields[sourceLayerId] = (
          state.tableFields[sourceLayerId] ?? []
        ).filter((column) => !fields.includes(column));
      },
      sync: (
        state,
        {
          payload: { tableFields, selectedTemplates },
        }: PayloadAction<{
          tableFields: Record<string, string[] | undefined>;
          selectedTemplates: Record<string, TableTemplate | undefined>;
        }>
      ) => {
        state.tableFields = tableFields;
        state.selectedTemplates = selectedTemplates;
      },
    },
  });

export const tableSlice = createTableSlice('table');
